import { RefObject, useEffect, useRef } from "react";

const SCROLL_THRESHOLD = 150;
const TOUCH_THRESHOLD = 50;

/**
 * Turns a section into a scroll-driven tab carousel.
 *
 * While the pointer is over `sectionRef`, wheel scrolling (and vertical swiping
 * on touch) steps through the tabs instead of moving the page. The page scroll
 * is only released once the user tries to go past the first or last tab, so the
 * section reads as a single stop on the way down the page rather than trapping
 * the visitor in it.
 *
 * `onChange` is read through a ref so callers can pass an inline arrow without
 * re-binding the listeners on every render.
 */
export const useScrollTabs = (
  sectionRef: RefObject<HTMLElement | null>,
  activeIndex: number,
  tabCount: number,
  onChange: (nextIndex: number) => void,
) => {
  const accumulatedDelta = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchStartX = useRef<number>(0);
  const isSwiping = useRef<boolean>(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Wheel scroll — cycle through tabs
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleWheel = (e: WheelEvent) => {
      accumulatedDelta.current += e.deltaY;

      if (Math.abs(accumulatedDelta.current) >= SCROLL_THRESHOLD) {
        const direction = accumulatedDelta.current > 0 ? 1 : -1;

        if (direction > 0) {
          if (activeIndex < tabCount - 1) {
            e.preventDefault();
            accumulatedDelta.current = 0;
            onChangeRef.current(activeIndex + 1);
          } else {
            accumulatedDelta.current = 0;
          }
        } else {
          if (activeIndex > 0) {
            e.preventDefault();
            accumulatedDelta.current = 0;
            onChangeRef.current(activeIndex - 1);
          } else {
            accumulatedDelta.current = 0;
          }
        }
      } else {
        const isAtStart = activeIndex === 0 && accumulatedDelta.current < 0;
        const isAtEnd = activeIndex === tabCount - 1 && accumulatedDelta.current > 0;
        if (!isAtStart && !isAtEnd) {
          e.preventDefault();
        }
      }
    };

    section.addEventListener("wheel", handleWheel, { passive: false });
    return () => section.removeEventListener("wheel", handleWheel);
  }, [sectionRef, activeIndex, tabCount]);

  // Touch swipe — cycle through tabs on mobile
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
      touchStartX.current = e.touches[0].clientX;
      isSwiping.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const deltaY = touchStartY.current - e.touches[0].clientY;
      const deltaX = Math.abs(touchStartX.current - e.touches[0].clientX);

      if (Math.abs(deltaY) > 10 && Math.abs(deltaY) > deltaX) {
        const isAtStart = activeIndex === 0 && deltaY < 0;
        const isAtEnd = activeIndex === tabCount - 1 && deltaY > 0;
        if (!isAtStart && !isAtEnd) {
          e.preventDefault();
          isSwiping.current = true;
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const deltaY = touchStartY.current - e.changedTouches[0].clientY;
      const deltaX = Math.abs(touchStartX.current - e.changedTouches[0].clientX);

      if (Math.abs(deltaY) < TOUCH_THRESHOLD || deltaX > Math.abs(deltaY)) {
        isSwiping.current = false;
        return;
      }

      if (deltaY > 0) {
        if (activeIndex < tabCount - 1) onChangeRef.current(activeIndex + 1);
      } else {
        if (activeIndex > 0) onChangeRef.current(activeIndex - 1);
      }
      isSwiping.current = false;
    };

    section.addEventListener("touchstart", handleTouchStart, { passive: true });
    section.addEventListener("touchmove", handleTouchMove, { passive: false });
    section.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      section.removeEventListener("touchstart", handleTouchStart);
      section.removeEventListener("touchmove", handleTouchMove);
      section.removeEventListener("touchend", handleTouchEnd);
    };
  }, [sectionRef, activeIndex, tabCount]);
};
